import { AlertTriangle, Check, Copy, KeyRound } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { cn } from './lib/cn'
import { useCopy } from './lib/hooks'
import { Button } from './primitives'
import { Checkbox } from './forms'
import { Modal } from './overlay'
import { ProgressBar } from './feedback'

/* ==========================================================================
 * Avatar / UserBadge
 * ======================================================================== */

function initialsFrom(name?: string | null, email?: string | null): string {
  const source = name?.trim() || email?.split('@')[0] || ''
  if (!source) return '?'

  const parts = source.split(/[\s._-]+/).filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase()
  }
  return source.slice(0, 2).toUpperCase()
}

export interface AvatarProps {
  name?: string | null
  email?: string | null
  src?: string | null
  size?: 'sm' | 'md' | 'lg'
  /** Draws a presence dot. */
  online?: boolean
  className?: string
}

const AVATAR_SIZE = {
  sm: 'h-7 w-7 text-[10px]',
  md: 'h-9 w-9 text-caption',
  lg: 'h-12 w-12 text-sm',
}

export function Avatar({ name, email, src, size = 'md', online, className }: AvatarProps) {
  const initials = useMemo(() => initialsFrom(name, email), [name, email])

  return (
    <span className={cn('relative inline-flex shrink-0', className)}>
      <span
        className={cn(
          'inline-flex items-center justify-center rounded-full border border-line bg-accent text-accent-foreground',
          'font-semibold uppercase',
          AVATAR_SIZE[size]
        )}
      >
        {src ? (
          <img src={src} alt="" className="h-full w-full rounded-full object-cover" />
        ) : (
          initials
        )}
      </span>
      {online !== undefined ? (
        <span
          aria-hidden
          className={cn(
            'absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-surface',
            online ? 'bg-success' : 'bg-subtle'
          )}
        />
      ) : null}
    </span>
  )
}

export interface UserBadgeProps {
  user?: { name?: string | null; email?: string | null; avatarUrl?: string | null } | null
  showName?: boolean
  className?: string
}

/**
 * The identity chip in the header: avatar, display name, email below.
 * Falls back to the email local-part, then to a neutral placeholder, so it
 * always renders something meaningful.
 */
export function UserBadge({ user, showName = true, className }: UserBadgeProps) {
  const displayName = user?.name?.trim() || user?.email?.split('@')[0] || 'Account'

  return (
    <span className={cn('flex items-center gap-2.5', className)}>
      <Avatar name={user?.name} email={user?.email} src={user?.avatarUrl} size="md" online />
      {showName ? (
        <span className="hidden min-w-0 flex-col text-left leading-tight sm:flex">
          <span className="truncate text-caption font-semibold text-foreground">{displayName}</span>
          <span className="truncate text-[11px] text-muted">{user?.email ?? ''}</span>
        </span>
      ) : null}
    </span>
  )
}

/** The identity block at the top of the account menu. */
export function UserMenuHeader({
  user,
}: {
  user?: { name?: string | null; email?: string | null } | null
}) {
  const displayName = user?.name?.trim() || user?.email?.split('@')[0] || 'Account'

  return (
    <div className="space-y-0.5">
      <p className="truncate text-caption font-semibold text-foreground">{displayName}</p>
      {user?.email ? (
        <p className="truncate text-[11px] text-muted">{user.email}</p>
      ) : null}
    </div>
  )
}

/* ==========================================================================
 * PasswordStrength
 * ======================================================================== */

export interface PasswordStrengthProps {
  password: string
  className?: string
}

const STRENGTH_LABELS = ['Too weak', 'Weak', 'Fair', 'Strong', 'Very strong'] as const

/**
 * Five-segment strength meter.
 *
 * Mirrors the server's signup rules (12 characters, mixed case, a digit and a
 * symbol) so the meter and the eventual validation error agree.
 */
export function PasswordStrength({ password, className }: PasswordStrengthProps) {
  const { score, checks } = useMemo(() => {
    const rules = {
      length: password.length >= 12,
      lower: /[a-z]/.test(password),
      upper: /[A-Z]/.test(password),
      digit: /[0-9]/.test(password),
      symbol: /[^A-Za-z0-9]/.test(password),
    }
    const passed = Object.values(rules).filter(Boolean).length

    // Length is load-bearing: a long passphrase outranks a short complex one.
    let level = passed
    if (password.length >= 16 && passed >= 4) level = 5
    if (password.length > 0 && passed <= 2) level = Math.min(level, 2)

    return { score: password.length === 0 ? 0 : Math.min(5, level), checks: rules }
  }, [password])

  const tone =
    score <= 2 ? 'bg-danger' : score === 3 ? 'bg-warning' : 'bg-success'
  const labelTone =
    score <= 2 ? 'text-danger-text' : score === 3 ? 'text-warning-text' : 'text-success-text'

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex gap-1" aria-hidden>
        {Array.from({ length: 5 }).map((_, index) => (
          <span
            key={index}
            className={cn(
              'h-1 flex-1 rounded-full transition-colors',
              index < score ? tone : 'bg-sunken'
            )}
          />
        ))}
      </div>

      <p className={cn('text-caption font-medium', password ? labelTone : 'text-subtle')}>
        {password ? STRENGTH_LABELS[score - 1] ?? 'Too weak' : 'Enter a password'}
      </p>

      {password ? (
        <ul className="grid grid-cols-2 gap-x-4 gap-y-1">
          {[
            ['12+ characters', checks.length],
            ['Lowercase letter', checks.lower],
            ['Uppercase letter', checks.upper],
            ['Number', checks.digit],
            ['Symbol', checks.symbol],
          ].map(([label, passed]) => (
            <li
              key={String(label)}
              className={cn(
                'flex items-center gap-1.5 text-[11px]',
                passed ? 'text-success-text' : 'text-subtle'
              )}
            >
              <Check className={cn('h-3 w-3 shrink-0', !passed && 'opacity-30')} />
              {label}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

/* ==========================================================================
 * OTPInput
 * ======================================================================== */

export interface OTPInputProps {
  length?: number
  value: string
  onValueChange: (value: string) => void
  onComplete?: (value: string) => void
  disabled?: boolean
  autoFocus?: boolean
  className?: string
  'aria-label'?: string
}

/**
 * Segmented one-time-passcode input. Supports typing, arrow navigation,
 * backspace, and pasting the whole code at once.
 */
export function OTPInput({
  length = 6,
  value,
  onValueChange,
  onComplete,
  disabled,
  autoFocus,
  className,
  ...aria
}: OTPInputProps) {
  const refs = useRef<Array<HTMLInputElement | null>>([])
  const digits = useMemo(
    () => Array.from({ length }, (_, index) => value[index] ?? ''),
    [value, length]
  )

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus()
  }, [autoFocus])

  const setAt = (index: number, char: string) => {
    const next = digits.slice()
    next[index] = char
    return next.join('').slice(0, length)
  }

  return (
    <div className={cn('flex gap-2', className)} {...aria}>
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            refs.current[index] = el
          }}
          value={digit}
          disabled={disabled}
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          aria-label={`Digit ${index + 1} of ${length}`}
          onChange={(event) => {
            const raw = event.currentTarget.value.replace(/\D/g, '')
            if (!raw) {
              onValueChange(setAt(index, ''))
              return
            }
            // Pasting into one box fills the remaining boxes.
            if (raw.length > 1) {
              const filled = (value.slice(0, index) + raw).slice(0, length)
              onValueChange(filled)
              const nextEmpty = Math.min(filled.length, length - 1)
              refs.current[nextEmpty]?.focus()
              if (filled.length === length) onComplete?.(filled)
              return
            }

            const next = setAt(index, raw)
            onValueChange(next)
            if (index < length - 1) refs.current[index + 1]?.focus()
            if (next.replace(/\D/g, '').length === length) onComplete?.(next)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Backspace' && !digits[index] && index > 0) {
              event.preventDefault()
              refs.current[index - 1]?.focus()
              onValueChange(setAt(index - 1, ''))
            } else if (event.key === 'ArrowLeft' && index > 0) {
              event.preventDefault()
              refs.current[index - 1]?.focus()
            } else if (event.key === 'ArrowRight' && index < length - 1) {
              event.preventDefault()
              refs.current[index + 1]?.focus()
            }
          }}
          onPaste={(event) => {
            event.preventDefault()
            const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
            if (!pasted) return
            onValueChange(pasted)
            refs.current[Math.min(pasted.length, length - 1)]?.focus()
            if (pasted.length === length) onComplete?.(pasted)
          }}
          className={cn(
            'h-12 w-11 rounded-control border border-line-strong bg-surface text-center',
            'font-mono text-lg font-semibold text-foreground',
            'transition-colors',
            'focus:border-focus focus:outline-none focus:ring-2 focus:ring-focus/40',
            'disabled:cursor-not-allowed disabled:bg-sunken disabled:text-subtle'
          )}
        />
      ))}
    </div>
  )
}

/* ==========================================================================
 * ApiKeyReveal — one-time secret
 * ======================================================================== */

export interface ApiKeyRevealProps {
  open: boolean
  /** The raw key. Only ever held in memory; never persisted by this component. */
  apiKey: string | null
  onClose: () => void
  /** Seconds before the countdown expires. */
  countdownSeconds?: number
  title?: string
}

/**
 * Shows a newly created secret exactly once.
 *
 * The close button stays disabled until the user confirms they copied it, the
 * value is never written to storage, and closing clears the parent's state.
 * The countdown is a prompt to copy now — it does not invalidate the key.
 */
export function ApiKeyReveal({
  open,
  apiKey,
  onClose,
  countdownSeconds = 60,
  title = 'Your new API key',
}: ApiKeyRevealProps) {
  const { copied, copy } = useCopy()
  const [acknowledged, setAcknowledged] = useState(false)
  const [remaining, setRemaining] = useState(countdownSeconds)

  useEffect(() => {
    if (!open) {
      setAcknowledged(false)
      setRemaining(countdownSeconds)
      return
    }

    setRemaining(countdownSeconds)
    const interval = setInterval(() => {
      setRemaining((value) => {
        if (value <= 1) {
          clearInterval(interval)
          return 0
        }
        return value - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [open, countdownSeconds])

  if (!apiKey) return null

  const progress = countdownSeconds > 0 ? (remaining / countdownSeconds) * 100 : 0

  return (
    <Modal
      open={open}
      onOpenChange={() => {
        /* Closing is gated on the checkbox below, not on Escape or backdrop. */
      }}
      size="md"
      dismissible={false}
      title={title}
      description="This value is shown once and cannot be retrieved later."
      footer={
        <Button disabled={!acknowledged} onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="space-y-5">
        <div className="flex items-start gap-2.5 rounded-card border border-warning/30 bg-warning-soft px-3.5 py-2.5">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning-text" />
          <p className="text-caption text-warning-text">
            Store this somewhere safe before closing. milliswift keeps only a
            hash — we cannot show it to you again.
          </p>
        </div>

        <div className="flex items-center gap-2 rounded-card border border-line bg-sunken p-3">
          <code className="ms-scroll min-w-0 flex-1 overflow-x-auto whitespace-nowrap font-mono text-caption text-foreground">
            {apiKey}
          </code>
          <Button
            size="sm"
            variant={copied ? 'outline' : 'primary'}
            leftIcon={copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            onClick={() => void copy(apiKey)}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>

        {countdownSeconds > 0 ? (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-caption">
              <span className="text-muted">
                {remaining > 0 ? 'This dialog closes in' : 'You can close this now'}
              </span>
              <span className="font-mono text-foreground">
                {`${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}`}
              </span>
            </div>
            <ProgressBar value={progress} />
          </div>
        ) : null}

        <div className="flex items-start gap-2.5">
          <Checkbox
            id="api-key-ack"
            checked={acknowledged}
            onCheckedChange={(checked) => setAcknowledged(checked === true)}
          />
          <label
            htmlFor="api-key-ack"
            className="cursor-pointer text-sm text-foreground"
          >
            I have copied my API key
          </label>
        </div>
      </div>
    </Modal>
  )
}

/* ==========================================================================
 * PlanCard
 * ======================================================================== */

export interface PlanCardProps {
  name: string
  /** Monthly price in cents. `null` renders "Contact sales". */
  priceCents: number | null
  features: string[]
  popular?: boolean
  current?: boolean
  ctaLabel?: string
  onSelect?: () => void
  href?: string
  className?: string
}

/**
 * One pricing tier.
 *
 * Prices come from the same source of truth as billing (the platform's plans
 * table), so the marketing site and the invoice cannot drift apart.
 */
export function PlanCard({
  name,
  priceCents,
  features,
  popular = false,
  current = false,
  ctaLabel,
  onSelect,
  href,
  className,
}: PlanCardProps) {
  const action = ctaLabel ?? (priceCents === null ? 'Contact sales' : 'Get started')

  return (
    <div
      className={cn(
        'relative flex flex-col rounded-card border bg-surface p-6 shadow-card transition-shadow',
        popular ? 'border-accent shadow-raised' : 'border-line',
        className
      )}
    >
      {popular ? (
        <span className="absolute -top-2.5 left-6 rounded-full bg-accent px-2.5 py-0.5 text-[11px] font-semibold text-accent-foreground">
          Most popular
        </span>
      ) : null}
      {current ? (
        <span className="absolute -top-2.5 left-6 rounded-full border border-line bg-raised px-2.5 py-0.5 text-[11px] font-semibold text-muted">
          Current plan
        </span>
      ) : null}

      <h3 className="text-sm font-semibold uppercase tracking-widest text-subtle">{name}</h3>

      <p className="mt-3 flex items-baseline gap-1">
        {priceCents === null ? (
          <span className="text-display font-semibold text-foreground">Custom</span>
        ) : (
          <>
            <span className="font-mono text-display font-semibold text-foreground">
              ${Math.round(priceCents / 100)}
            </span>
            <span className="text-caption text-muted">/month</span>
          </>
        )}
      </p>

      <ul className="mt-6 flex-1 space-y-2.5">
        {features.map((feature) => (
          <li key={feature} className="flex items-start gap-2 text-sm text-muted">
            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success-text" />
            {feature}
          </li>
        ))}
      </ul>

      <div className="mt-6">
        {href ? (
          <a
            href={href}
            className={cn(
              'flex h-10 w-full items-center justify-center rounded-control text-sm font-medium transition-colors',
              popular
                ? 'bg-primary text-primary-foreground hover:bg-primary-hover'
                : 'border border-line-strong text-foreground hover:bg-sunken'
            )}
          >
            {action}
          </a>
        ) : (
          <Button
            className="w-full"
            variant={popular ? 'primary' : 'outline'}
            onClick={onSelect}
            disabled={current}
            leftIcon={current ? <KeyRound className="h-4 w-4" /> : undefined}
          >
            {current ? 'Current plan' : action}
          </Button>
        )}
      </div>
    </div>
  )
}
